import type { DriveStep } from "driver.js";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "@/components/ds/icons";
import { PageHeader } from "@/components/ui/PageHeader";
import { CapacitacionesDocumentosPanel } from "./CapacitacionesDocumentosPanel";
import { apiCapacitacionesDocumentosAdapter } from "@/lib/capacitaciones-documentos-adapter";
import { useGuide } from "@/hooks/useGuide";
import { useRegisterPageGuide } from "@/contexts/ActiveGuideContext";
import { useGuideAutostart } from "@/hooks/useGuideAutostart";
import { usePendingGuide } from "@/hooks/usePendingGuide";

function buildCapacitacionesGuideSteps(): DriveStep[] {
  return [
    // Paso 1 — Popover centrado de apertura (sin elemento anclado)
    {
      popover: {
        title: "Bienvenido a Documentos Empresa",
        description:
          "Este es tu espacio de Documentos Empresa. Aquí subes los documentos institucionales de tu empresa y eliges cuáles pueden usarse como fuente para generar capacitaciones.",
      },
    },
    // Paso 2 — Highlight de la lista de documentos
    {
      element: '[data-tour="capacitaciones-lista"]',
      popover: {
        title: "Tus documentos",
        description:
          "Estos son los documentos institucionales de tu empresa. Cada fila corresponde a un documento que puedes cargar.",
      },
    },
    // Paso 3 — Highlight del control de subida (fila de ejemplo)
    {
      element: '[data-tour="capacitaciones-subir"]',
      popover: {
        title: "Sube cada documento",
        description: "Sube cada documento desde aquí: PDF, Word, Excel o imagen.",
      },
    },
    // Paso 4 — Highlight del switch "Fuente de capacitación" (fila de ejemplo).
    // Antes anclaba en el botón "Capacitaciones IA" de la fila, que se eliminó:
    // los programas ya no se generan desde esta pantalla sino desde el módulo
    // Capacitaciones. Un `element` inexistente deja el paso sin anclar.
    {
      element: '[data-tour="capacitaciones-fuente"]',
      popover: {
        title: "Marca las fuentes de capacitación",
        description:
          "Activa este interruptor en los documentos que sirvan para capacitar. Los que queden marcados aparecen disponibles al crear un programa desde Capacitaciones; el resto queda sólo como respaldo documental.",
      },
    },
    // Paso 5 — Highlight de la exportación de respaldo
    {
      element: '[data-tour="capacitaciones-exportar"]',
      popover: {
        title: "Descarga tu respaldo",
        description:
          "Descarga en un solo archivo ZIP todos los documentos que tienes cargados, ordenados por categoría. Sirve para responder una fiscalización sin ir documento por documento.",
      },
    },
    // Paso 6 — Popover de cierre (sin elemento anclado)
    {
      popover: {
        title: "¡Listo!",
        description:
          "Ya sabes cómo gestionar tus documentos de empresa. Puedes reabrir esta guía cuando quieras haciendo clic en el botón de ayuda en la parte superior de la página.",
      },
    },
  ];
}

export function Capacitaciones() {
  const [exportando, setExportando] = useState(false);

  // --- Guía de Capacitaciones ---
  const { startGuide } = useGuide("guide-capacitaciones", { buildSteps: buildCapacitacionesGuideSteps });
  useRegisterPageGuide(startGuide);
  usePendingGuide("guide-capacitaciones", startGuide, { anchorSelector: '[data-tour="capacitaciones-lista"]' });
  useGuideAutostart("guide-capacitaciones", startGuide, { anchorSelector: '[data-tour="capacitaciones-lista"]' });
  // --- fin Guía de Capacitaciones ---

  // El ZIP se arma streameado del lado del servidor y puede tardar varios
  // segundos con muchos documentos: el botón queda deshabilitado con spinner
  // hasta que el blob llegó completo.
  async function handleExportar() {
    if (exportando) return;
    setExportando(true);
    try {
      await apiCapacitacionesDocumentosAdapter.exportarZip();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo generar el respaldo.");
    } finally {
      setExportando(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Documentos Empresa"
        subtitle="Checklist del DS 76 y la Ley 16.744. Marca lo que aplica a tu operación."
        rightContent={
          <Button
            variant="outline"
            className="gap-2"
            disabled={exportando}
            onClick={handleExportar}
            data-tour="capacitaciones-exportar"
          >
            {exportando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Exportar respaldo
          </Button>
        }
      />
      <CapacitacionesDocumentosPanel />
    </div>
  );
}
